import { API_ENDPOINTS } from '@/src/constants';
import { useAuth } from '@/src/contexts';
import { djangoClient } from '@/src/helpers';
import { useQuery } from '@tanstack/react-query';
import { BondPriceQueryKeys, BondPriceResponse, Currency } from './bond-price.types';

interface UseBondPriceParams {
  bondId: string | undefined;
  currency: Currency;
  enabled?: boolean;
}

/**
 * Get bond price history from the Django API
 * @param bondId Bond ID
 * @param currency Currency (USD or AUD)
 */
export const useBondPrice = ({ bondId, currency, enabled = true }: UseBondPriceParams) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: [BondPriceQueryKeys.BondPrice, bondId, currency],
    queryFn: async () => {
      if (!bondId) {
        throw new Error('Bond ID is required');
      }

      const response = await djangoClient.get<BondPriceResponse>(
        `${API_ENDPOINTS.BOND_PRICE}?bond=${bondId}&currency=${currency}`
      );
      return response.data;
    },
    enabled: enabled && !!user?.email && !!bondId, // Only fetch when user is authenticated and bondId is available
    staleTime: 5 * 60 * 1000, // 5 minutes - price data changes relatively slowly
    gcTime: 10 * 60 * 1000, // 10 minutes cache
  });
};
